import { useEffect, useRef } from 'react';
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import History from '@mui/icons-material/History';
import BoltOutlined from '@mui/icons-material/BoltOutlined';
import type { MatchedCommand } from './commands/types';
import type { SlashCommandGroupedResult } from '@/hooks/search/useSlashCommands';

interface SlashCommandResultsProps {
  grouped: SlashCommandGroupedResult;
  flat: MatchedCommand[];
  selectedIndex: number;
  query: string;
  onSelect: (command: MatchedCommand) => void;
}

interface GroupSection {
  key: 'actions' | 'related' | 'history' | 'recent' | 'create' | 'navigation' | 'utilities';
  labelKey: string;
  labelDefault: string;
  items: MatchedCommand[];
}

export function SlashCommandResults({
  grouped,
  flat,
  selectedIndex,
  query,
  onSelect,
}: SlashCommandResultsProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const allSections: GroupSection[] = [
    { key: 'actions', labelKey: 'slashCommand.group.actions', labelDefault: 'Actions on this record', items: grouped.actions },
    { key: 'related', labelKey: 'slashCommand.group.related', labelDefault: 'Show related', items: grouped.related },
    { key: 'history', labelKey: 'slashCommand.group.history', labelDefault: 'History', items: grouped.history },
    { key: 'recent', labelKey: 'slashCommand.group.recent', labelDefault: 'Recent', items: grouped.recent },
    { key: 'create', labelKey: 'slashCommand.group.create', labelDefault: 'Create', items: grouped.create },
    { key: 'navigation', labelKey: 'slashCommand.group.navigation', labelDefault: 'Navigation', items: grouped.navigation },
    { key: 'utilities', labelKey: 'slashCommand.group.utilities', labelDefault: 'Utilities', items: grouped.utilities },
  ];
  const sections = allSections.filter((s) => s.items.length > 0);

  useEffect(() => {
    if (!containerRef.current) return;
    const selectedElement = containerRef.current.querySelector(
      `[data-command-index="${selectedIndex}"]`,
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  if (flat.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {query
            ? t('slashCommand.noMatch', 'No commands match "{{query}}"', { query })
            : t('slashCommand.emptyState', 'Type a command name, or browse below')}
        </Typography>
      </Box>
    );
  }

  let flatCursor = 0;

  return (
    <Box ref={containerRef} sx={{ p: 1, overflow: 'auto', height: '100%' }}>
      {sections.map((section) => (
        <Box key={section.key} sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
            {section.key === 'recent' && (
              <History fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
            {section.key === 'actions' && (
              <BoltOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {t(section.labelKey, section.labelDefault)}
            </Typography>
          </Box>

          <List dense disablePadding>
            {section.items.map((matched) => {
              const currentIndex = flatCursor;
              const isSelected = currentIndex === selectedIndex;
              flatCursor++;

              const Icon = matched.command.icon;

              return (
                <ListItemButton
                  key={matched.command.id}
                  data-command-index={currentIndex}
                  selected={isSelected}
                  onClick={() => onSelect(matched)}
                  sx={{
                    borderRadius: 1,
                    mx: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      '&:hover': {
                        bgcolor: 'action.selected',
                      },
                    },
                  }}
                >
                  {Icon && (
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {matched.localizedTitle}
                      </Typography>
                    }
                    secondary={
                      matched.localizedSubtitle ? (
                        <Typography variant="caption" color="text.secondary">
                          {matched.localizedSubtitle}
                        </Typography>
                      ) : undefined
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
}
